import { PI2 } from '../math';
import { Bounding } from '../modifiers/Adapt';
import { ShapePrimitive } from '../shapes/ShapePrimitive';
import { ShapeBase } from './ShapeBase';
/**
 * Create a shape from loop
 *
 * @category Shapes.Primitives
 * @public
 * @class ShapeLoop
 * @extends {ShapePrimitive}
 */
class ShapeLoop extends ShapePrimitive {
    /**
     * Creates an instance of ShapeLoop.
     *
     * @param {IShapeLoopSettings} [settings={}]
     * @param {boolean} [bPreventGeneration=false]
     */
    constructor(settings = {}, bPreventGeneration = false) {
        settings.type = settings.type || 'ShapeLoop';
        super(settings);
        this.loopDependencies = (settings.loopDependencies || []).concat('sideLength');
        this.props.loop = settings.loop;
        if (!bPreventGeneration) {
            this.loop = {
                start: 0,
                end: PI2,
                inc: PI2 / 10,
                vertex: () => [0, 0],
            };
            this.bStaticLoop = this.isStaticLoop();
            this.bStatic = this.isStatic();
            this.bStaticIndexed = this.isStaticIndexed();
        }
    }
    /**
     * Check if currentOrSingleLoopBuffer is static
     *
     * @returns {boolean}
     */
    isStaticLoop() {
        if (this.loopDependencies.includes('propArguments'))
            return false;
        for (let i = 0, len = this.loopDependencies.length; i < len; i++)
            if (typeof this.props[this.loopDependencies[i]] === 'function')
                return false;
        return true;
    }
    /**
     * Check if shape is static
     *
     * @returns {boolean}
     */
    isStatic() {
        return this.bStaticLoop && super.isStatic();
    }
    /**
     * Check if shape has static indexed
     * The number of vertices is defined by number of loop iteration
     *
     * @returns {boolean}
     */
    isStaticIndexed() {
        // return this.bStaticLoop && super.isStaticIndexed()
        return (super.isStaticIndexed() &&
            (typeof this.props.loop !== 'undefined'
                ? typeof this.props.loop.start !== 'function' &&
                    typeof this.props.loop.end !== 'function' &&
                    typeof this.props.loop.inc !== 'function'
                : true));
    }
    /**
     *  Unset buffer
     *
     * @param {boolean} [bClearIndexed=false]
     * @param {boolean} [bPropagateToParents=false]
     */
    clearBuffer(bClearIndexed = false, bPropagateToParents = true) {
        this.bStaticLoop = this.isStaticLoop();
        if (bClearIndexed) {
            this.currentOrSingleLoopBuffer = undefined;
        }
        super.clearBuffer(bClearIndexed, bPropagateToParents);
    }
    /**
     * Set single or multiple props
     *
     * @param {(Props)} key
     * @param {*} [value]
     * @param {boolean} [bClearIndexed=false]
     */
    setProp(key, value) {
        let bClearIndexed = false;
        const keys = (typeof key === 'string' ? { [key]: value } : key);
        for (let i = this.loopDependencies.length - 1; i >= 0; i--) {
            if (this.loopDependencies[i] in keys) {
                // this.props.loop = undefined
                bClearIndexed = true;
                break;
            }
        }
        if ('loop' in keys) {
            keys.loop = { ...this.props.loop, ...keys.loop };
            bClearIndexed = true;
        }
        super.setProp(keys, value, bClearIndexed);
    }
    /**
     * Return length of buffer
     *
     * @param {PropArguments} [propArguments]
     * @returns {number}
     */
    getBufferLength(propArguments) {
        if (this.bStatic && typeof this.buffer !== 'undefined')
            return this.buffer.length;
        if (this.bStaticLoop && typeof this.currentOrSingleLoopBuffer !== 'undefined')
            return this.currentOrSingleLoopBuffer.length * this.getRepetitionCount();
        const { count } = this.getLoop(propArguments || ShapeBase.getEmptyPropArguments(this));
        return this.getRepetitionCount() * count * 2;
    }
    /**
     * Return a buffer of children shape or loop generated buffer
     *
     * @protected
     * @param {number} generateId
     * @param {PropArguments} propArguments
     * @returns {Float32Array}
     */
    generateBuffer(generateId, propArguments) {
        if (!this.bStaticLoop)
            return this.generateLoopBuffer(propArguments);
        if (typeof this.props.sideLength === 'function' || typeof this.currentOrSingleLoopBuffer === 'undefined')
            this.currentOrSingleLoopBuffer = this.generateLoopBuffer(propArguments);
        return this.currentOrSingleLoopBuffer;
    }
    /**
     * Generate loop buffer
     *
     * @protected
     * @param {PropArguments} propArguments
     * @returns {Float32Array}
     */
    generateLoopBuffer(propArguments) {
        const loopMeta = this.getLoop(propArguments);
        const { start, inc, /*end,*/ count } = loopMeta;
        const sideLength = this.getRepetitionSideLength(propArguments);
        const getVertex = (this.props.loop && this.props.loop.vertex ? this.props.loop.vertex : this.loop.vertex);
        const shapeLoop = {
            index: 0,
            offset: 0,
            current: 0,
            count: count,
        };
        const vertexLength = shapeLoop.count;
        const bufferLength = vertexLength * 2;
        const currentOrSingleLoopBuffer = new Float32Array(bufferLength);
        for (let i = 0, j = 0; i < vertexLength; i++, j += 2) {
            const current = start + inc * i;
            const offset = shapeLoop.count > 1 ? i / (shapeLoop.count - 1) : 1;
            // const angle = (end - start) * offset + start
            shapeLoop.current = current;
            shapeLoop.index = i + 1;
            shapeLoop.offset = offset;
            const vertex = getVertex(shapeLoop, propArguments, loopMeta);
            currentOrSingleLoopBuffer[j] = vertex[0];
            currentOrSingleLoopBuffer[j + 1] = vertex[1];
            // currentOrSingleLoopBuffer[j] *= sideLength[0]
            // currentOrSingleLoopBuffer[j + 1] *= sideLength[1]
            // Bounding.add(tmpBounding, currentOrSingleLoopBuffer[j], currentOrSingleLoopBuffer[j + 1])
        }
        const tmpBounding = [undefined, undefined, undefined, undefined];
        const buffer = this.applyModifiers(currentOrSingleLoopBuffer, propArguments);
        for (let i = 0, len = buffer.length; i < len; i += 2) {
            buffer[i] = buffer[i] * sideLength[0];
            buffer[i + 1] = buffer[i + 1] * sideLength[1];
            Bounding.add(tmpBounding, buffer[i], buffer[i + 1]);
        }
        Bounding.bind(this.currentGenerationPrimitiveBounding, tmpBounding);
        return buffer;
    }
    /**
     * Return information about a client loop gnerator
     *
     * @public
     * @param {PropArguments} propArguments
     * @returns {ShapeLoopInformation}
     */
    getLoop(propArguments) {
        let start = this.props.loop?.start ?? this.loop.start;
        let end = this.props.loop?.end ?? this.loop.end;
        let inc = this.props.loop?.inc ?? this.loop.inc;
        start = (typeof start === 'function' ? start(propArguments) : start);
        end = (typeof end === 'function' ? end(propArguments) : end);
        inc = (typeof inc === 'function' ? inc(propArguments) : inc);
        const count = Math.ceil((end - start) / inc);
        return { start, end, inc, count: count <= 0 ? 0 : count };
    }
    /**
     * Set shape from loop generator
     *
     * @param {(IShapeLoopGenerator)} [shape]
     */
    setShape(loop) {
        this.setProp('loop', loop);
    }
    /**
     * Get static buffer
     *
     * @param sideLength
     * @returns
     */
    static getBuffer(props) {
        const shape = new this({ ...props, sideLength: props?.sideLength || 1 });
        shape.generate();
        return shape.getBuffer() || new Float32Array();
    }
}
ShapeLoop.PId2 = Math.PI / 2;
export { ShapeLoop };
//# sourceMappingURL=ShapeLoop.js.map